(() => {
  if (window.EngravingHero) return;

  const ERASE_MS = 320;
  const AUTO_DELAY_MS = 2600;
  const TYPE_DEBOUNCE_MS = 450;
  const FRAME_MS = 1000 / 60;
  const MAX_PARTICLES = 400;
  const SPARK_TINT = [255, 225, 120];
  const SMOKE = [150, 140, 130];
  const HOT_CORE = [255, 255, 240];

  const instances = new Map();

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  function hexToRgb(hex) {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return [0, 0, 0];
    let h = m[1];
    if (h.length === 3) h = h.replace(/./g, (c) => c + c);
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgba = (c, a) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;
  const shade = (c, k) => c.map((v) => Math.min(255, v * k));
  const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);

  // Seeded so the char texture is identical when the canvas is redrawn after a resize.
  function seededRandom(str) {
    let a = 2166136261;
    for (const ch of str) a = Math.imul(a ^ ch.charCodeAt(0), 16777619);
    return () => {
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  class EngravingHero {
    constructor(root) {
      this.root = root;
      const cfg = JSON.parse(root.querySelector('[data-eh-config]').textContent);
      this.cfg = cfg;

      this.IW = cfg.imageWidth;
      this.IH = cfg.imageHeight;
      this.H = {
        cx: (cfg.centerX / 100) * this.IW,
        cy: (cfg.centerY / 100) * this.IH,
        ang: (cfg.angle * Math.PI) / 180,
        maxW: (cfg.maxWidth / 100) * this.IW,
        maxH: (cfg.maxHeight / 100) * this.IH,
      };
      this.laser = hexToRgb(cfg.laserColor);
      this.burnRgb = hexToRgb(cfg.burnColor);
      this.fontStack = `"${cfg.fontFamily}", "Albert Sans", "Helvetica Neue", Arial, sans-serif`;

      const q = (sel) => root.querySelector(sel);
      this.photo = q('[data-eh-photo]');
      this.burn = q('[data-eh-burn]');
      this.fx = q('[data-eh-fx]');
      this.input = q('[data-eh-input]');
      this.count = q('[data-eh-count]');
      this.status = q('[data-eh-status]');
      this.stateEl = q('[data-eh-state]');
      this.currentEl = q('[data-eh-current]');
      this.form = q('[data-eh-form]');
      this.error = q('[data-eh-error]');
      this.isCart = !!(this.form && this.form.hasAttribute('data-eh-cart'));
      this.chips = Array.from(root.querySelectorAll('[data-eh-chip]'));

      this.bctx = this.burn.getContext('2d');
      this.fctx = this.fx.getContext('2d');
      this.off = document.createElement('canvas');
      this.octx = this.off.getContext('2d');

      const presets = this.chips.map((c) => c.dataset.ehChip);
      this.autoList = presets.length ? [...presets, cfg.defaultText] : [];

      this.text = this.input.value.trim();
      this.job = null;
      this.particles = [];
      this.auto = true;
      this.autoIdx = 0;
      this.autoTimer = null;
      this.autoPending = false;
      this.debounce = null;
      this.raf = null;
      this.lastFrame = null;
      this.visible = true;
      this.pausedAt = null;
      this.ready = false;
      this.destroyed = false;
      this.scale = 1;

      this.reduceMql = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduce = this.reduceMql.matches;

      this.bindEvents();
      this.sizeCanvases();

      const fontLoad = document.fonts
        ? document.fonts.load(this.font(50), this.text || 'A').catch(() => {})
        : Promise.resolve();
      fontLoad.then(() => {
        if (this.destroyed) return;
        this.ready = true;
        this.showStatic();
        this.scheduleAuto();
      });
    }

    bindEvents() {
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };

      this.input.addEventListener('input', () => {
        this.stopAuto();
        this.updateCount();
        this.clearError();
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.engrave(this.input.value), TYPE_DEBOUNCE_MS);
      }, opts);
      this.input.addEventListener('focus', () => this.stopAuto(), opts);

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.stopAuto();
          clearTimeout(this.debounce);
          this.setInputValue(chip.dataset.ehChip);
          this.engrave(this.input.value);
        }, opts);
      });

      if (this.form) {
        this.form.addEventListener('pointerdown', () => this.stopAuto(), opts);
        this.form.addEventListener('focusin', () => this.stopAuto(), opts);
        if (this.isCart) {
          this.form.noValidate = true;
          this.form.addEventListener('cart:error', (e) => {
            const d = e.detail || {};
            this.showError(d.error || d.message || '');
          }, opts);
        }
      }
      // Capture phase on the root runs before the theme's <product-form> submit handler on the form.
      this.root.addEventListener('submit', (e) => this.onSubmit(e), { capture: true, signal: this.ac.signal });

      this.ro = new ResizeObserver(() => this.onResize());
      this.ro.observe(this.photo);

      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
        this.updatePause();
      }, { rootMargin: '100px 0px' });
      this.io.observe(this.root);

      document.addEventListener('visibilitychange', () => this.updatePause(), opts);

      this.reduceMql.addEventListener('change', (e) => {
        this.reduce = e.matches;
        if (!this.ready) return;
        if (this.reduce) {
          clearTimeout(this.autoTimer);
          this.particles = [];
          this.clearFx();
          this.showStatic();
        } else {
          this.scheduleAuto();
        }
      }, opts);
    }

    destroy() {
      this.destroyed = true;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      clearTimeout(this.autoTimer);
      clearTimeout(this.debounce);
      this.ac.abort();
      this.ro.disconnect();
      this.io.disconnect();
      instances.delete(this.root);
    }

    font(size) {
      return `${this.cfg.fontWeight} ${size}px ${this.fontStack}`;
    }

    // ---- sizing: all drawing happens in the image's natural pixel space, scaled to the backing store
    sizeCanvases() {
      const cssW = this.photo.getBoundingClientRect().width;
      if (!cssW) return false;
      const dpr = clamp(window.devicePixelRatio || 1, 2, 2.5);
      const w = Math.round(cssW * dpr);
      if (w === this.burn.width) return false;
      const h = Math.round((w * this.IH) / this.IW);
      [this.burn, this.fx, this.off].forEach((c) => {
        c.width = w;
        c.height = h;
      });
      this.scale = w / this.IW;
      return true;
    }

    onResize() {
      if (!this.sizeCanvases() || !this.ready || !this.job) return;
      if (this.job.phase === 'done' || this.job.phase === 'engrave') {
        this.job.box = this.renderText(this.text);
      }
      if (this.job.phase === 'done') this.drawReveal(this.job.box, this.job.rows, null);
      else this.kick();
    }

    // ---- rendering
    renderText(text) {
      const o = this.octx;
      const { H, scale: s } = this;
      o.setTransform(1, 0, 0, 1, 0, 0);
      o.clearRect(0, 0, this.off.width, this.off.height);
      if (!text) return null;

      o.setTransform(s, 0, 0, s, 0, 0);
      o.translate(H.cx, H.cy);
      o.rotate(H.ang);
      o.textAlign = 'left';
      o.textBaseline = 'alphabetic';

      // Size from the font's cap height, not this text's ink, so every engraving shares one letter size.
      o.font = this.font(100);
      const capPerPx = o.measureText('H').actualBoundingBoxAscent / 100;
      let size = (H.maxH * this.cfg.textSize) / 100 / capPerPx;
      o.font = this.font(size);
      let m = o.measureText(text);
      const inkW0 = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
      if (inkW0 > H.maxW) {
        size *= H.maxW / inkW0;
        o.font = this.font(size);
        m = o.measureText(text);
      }

      const asc = m.actualBoundingBoxAscent;
      const desc = m.actualBoundingBoxDescent;
      const left = m.actualBoundingBoxLeft;
      const right = m.actualBoundingBoxRight;
      const cap = capPerPx * size;
      const halfW = H.maxW / 2;
      const halfH = H.maxH / 2;

      let x = -(right - left) / 2;
      if (this.cfg.alignX === 'left') x = -halfW + left;
      else if (this.cfg.alignX === 'right') x = halfW - right;

      let y = cap / 2;
      if (this.cfg.alignY === 'top') y = -halfH + cap;
      else if (this.cfg.alignY === 'bottom') y = halfH;

      const burn = this.burnRgb;
      o.shadowColor = rgba(shade(burn, 2.6), 0.55);
      o.shadowBlur = 5 * s;
      o.fillStyle = rgba(shade(burn, 2), 0.55);
      o.fillText(text, x, y);
      o.shadowColor = rgba(shade(burn, 0.87), 0.6);
      o.shadowBlur = 1.2 * s;
      o.fillStyle = rgba(burn, 0.92);
      o.fillText(text, x, y);
      o.shadowBlur = 0;
      o.shadowColor = 'transparent';

      const inkX = x - left;
      const inkY = y - asc;
      const inkW = left + right;
      const inkH = asc + desc;
      const rand = seededRandom(`${text}|${size.toFixed(2)}`);
      const dark = rgba(shade(burn, 0.43), 0.35);
      const light = rgba(shade(burn, 3.3), 0.25);
      o.globalCompositeOperation = 'source-atop';
      const dots = Math.min(20000, (inkW * inkH) / 5);
      for (let i = 0; i < dots; i++) {
        o.fillStyle = rand() < 0.5 ? dark : light;
        o.fillRect(inkX + rand() * inkW, inkY + rand() * inkH, 0.8, 0.8);
      }
      o.globalCompositeOperation = 'source-over';

      const pad = 3;
      return { x: inkX - pad, y: inkY - pad, w: inkW + pad * 2, h: inkH + pad * 2 };
    }

    drawReveal(box, rowsDone, partial) {
      const b = this.bctx;
      const { H, scale: s } = this;
      b.setTransform(1, 0, 0, 1, 0, 0);
      b.clearRect(0, 0, this.burn.width, this.burn.height);
      if (!box) return;
      const rh = box.h / this.job.rows;
      b.save();
      b.setTransform(s, 0, 0, s, 0, 0);
      b.translate(H.cx, H.cy);
      b.rotate(H.ang);
      b.beginPath();
      b.rect(box.x, box.y, box.w, rowsDone * rh);
      if (partial) b.rect(partial.x0, box.y + rowsDone * rh, partial.x1 - partial.x0, rh + 0.4);
      b.clip();
      b.setTransform(1, 0, 0, 1, 0, 0);
      b.drawImage(this.off, 0, 0);
      b.restore();
    }

    clearFx() {
      this.fctx.setTransform(1, 0, 0, 1, 0, 0);
      this.fctx.clearRect(0, 0, this.fx.width, this.fx.height);
    }

    toImage(lx, ly) {
      const { cx, cy, ang } = this.H;
      const c = Math.cos(ang);
      const sn = Math.sin(ang);
      return [cx + lx * c - ly * sn, cy + lx * sn + ly * c];
    }

    drawLaser(x, y, now) {
      const f = this.fctx;
      const L = this.laser;
      const beam = f.createLinearGradient(x, 0, x, y);
      beam.addColorStop(0, rgba(L, 0));
      beam.addColorStop(1, rgba(L, 0.55));
      f.strokeStyle = beam;
      f.lineWidth = 0.9;
      f.beginPath();
      f.moveTo(x, 0);
      f.lineTo(x, y);
      f.stroke();

      const r = 14 * (1 + Math.sin(now / 23) * 0.15);
      const spot = f.createRadialGradient(x, y, 0, x, y, r);
      spot.addColorStop(0, rgba(HOT_CORE, 1));
      spot.addColorStop(0.18, rgba(mix(L, SPARK_TINT, 0.6), 0.95));
      spot.addColorStop(0.5, rgba(L, 0.35));
      spot.addColorStop(1, rgba(L, 0));
      f.fillStyle = spot;
      f.beginPath();
      f.arc(x, y, r, 0, Math.PI * 2);
      f.fill();
    }

    // ---- state
    setStatus(state, live) {
      this.stateEl.textContent = state;
      this.currentEl.textContent = `“${this.text}”`;
      this.status.classList.toggle('is-live', live);
    }

    showStatic() {
      this.job = { phase: 'done', rows: 1, box: null };
      this.job.box = this.renderText(this.text);
      this.drawReveal(this.job.box, 1, null);
      this.burn.style.opacity = '1';
      this.setStatus(this.cfg.strings.engraved, false);
    }

    engrave(value) {
      this.text = value.trim();
      if (!this.ready) return;
      if (this.reduce) {
        this.showStatic();
        return;
      }
      if (!this.job || this.job.phase !== 'erase') {
        const hasInk = this.job && this.job.box && this.burn.style.opacity !== '0';
        this.job = { phase: hasInk ? 'erase' : 'start', t0: performance.now() };
      }
      this.setStatus(this.cfg.strings.engraving, true);
      this.kick();
    }

    startEngrave(now) {
      const box = this.renderText(this.text);
      this.burn.style.opacity = '1';
      if (!box) {
        this.job = { phase: 'done', rows: 1, box: null };
        this.drawReveal(null);
        this.finishEngrave();
        return;
      }
      const rows = Math.max(14, Math.round(box.h / 1.7));
      const dur = Math.min(3200, 1300 + box.w * 3.2);
      this.job = { phase: 'engrave', t0: now, dur, box, rows };
    }

    finishEngrave() {
      this.job.phase = 'done';
      this.setStatus(this.cfg.strings.engraved, false);
      this.scheduleAuto();
    }

    // ---- animation loop
    get paused() {
      return !this.visible || document.hidden;
    }

    kick() {
      if (this.raf || this.paused || this.destroyed) return;
      this.raf = requestAnimationFrame((t) => this.tick(t));
    }

    updatePause() {
      if (this.paused) {
        if (this.pausedAt === null) this.pausedAt = performance.now();
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
        return;
      }
      if (this.pausedAt === null) return;
      if (this.job && this.job.t0) this.job.t0 += performance.now() - this.pausedAt;
      this.pausedAt = null;
      this.lastFrame = null;
      this.kick();
      if (this.autoPending) {
        this.autoPending = false;
        this.scheduleAuto();
      }
    }

    tick(now) {
      this.raf = null;
      const step = this.lastFrame === null ? 1 : clamp((now - this.lastFrame) / FRAME_MS, 0.25, 3);
      this.lastFrame = now;

      const f = this.fctx;
      this.clearFx();
      f.setTransform(this.scale, 0, 0, this.scale, 0, 0);
      let busy = false;
      const job = this.job;

      if (job && job.phase === 'erase') {
        busy = true;
        const p = Math.min(1, (now - job.t0) / ERASE_MS);
        this.burn.style.opacity = String(1 - p);
        if (p >= 1) {
          this.drawReveal(null);
          job.phase = 'start';
        }
      }
      if (job && job.phase === 'start') this.startEngrave(now);

      const cur = this.job;
      if (cur && cur.phase === 'engrave') {
        busy = true;
        const { box, rows, dur } = cur;
        const p = Math.min(1, (now - cur.t0) / dur);
        const rf = p * rows;
        const r = Math.min(rows - 1, Math.floor(rf));
        const t = rf - r;
        const ltr = r % 2 === 0;
        const lx = ltr ? box.x + t * box.w : box.x + (1 - t) * box.w;
        const partial = ltr ? { x0: box.x, x1: lx } : { x0: lx, x1: box.x + box.w };
        this.drawReveal(box, p >= 1 ? rows : r, p >= 1 ? null : partial);

        if (p < 1) {
          const [px, py] = this.toImage(lx, box.y + (r + 0.5) * (box.h / rows));
          this.drawLaser(px, py, now);
          if (this.particles.length < MAX_PARTICLES) {
            if (Math.random() < 0.7 * step) {
              this.particles.push({ x: px, y: py, vx: (Math.random() - 0.5) * 1.6, vy: -Math.random() * 1.8 - 0.4, life: 1, smoke: false, tint: Math.random() * 0.8 });
            }
            if (Math.random() < 0.25 * step) {
              this.particles.push({ x: px, y: py - 2, vx: (Math.random() - 0.5) * 0.3, vy: -0.5 - Math.random() * 0.4, life: 1, smoke: true, r: 3 + Math.random() * 4 });
            }
          }
        } else {
          this.finishEngrave();
        }
      }

      this.particles = this.particles.filter((pt) => pt.life > 0);
      for (const pt of this.particles) {
        pt.x += pt.vx * step;
        pt.y += pt.vy * step;
        if (pt.smoke) {
          pt.life -= 0.012 * step;
          pt.r += 0.12 * step;
          f.fillStyle = rgba(SMOKE, 0.1 * Math.max(0, pt.life));
          f.beginPath();
          f.arc(pt.x, pt.y, pt.r, 0, Math.PI * 2);
          f.fill();
        } else {
          pt.vy += 0.09 * step;
          pt.life -= 0.05 * step;
          f.fillStyle = rgba(mix(this.laser, SPARK_TINT, pt.tint), Math.max(0, pt.life));
          f.fillRect(pt.x, pt.y, 1.3, 1.3);
        }
      }

      if (busy || this.particles.length) this.kick();
      else this.lastFrame = null;
    }

    // ---- auto-play of the preset texts until the visitor takes over
    scheduleAuto() {
      clearTimeout(this.autoTimer);
      if (!this.auto || this.reduce || this.autoList.length < 2) return;
      this.autoTimer = setTimeout(() => {
        if (!this.auto) return;
        if (this.paused) {
          this.autoPending = true;
          return;
        }
        const next = this.autoList[this.autoIdx++ % this.autoList.length];
        this.setInputValue(next);
        this.engrave(this.input.value);
      }, AUTO_DELAY_MS);
    }

    stopAuto() {
      if (!this.auto) return;
      this.auto = false;
      this.autoPending = false;
      clearTimeout(this.autoTimer);
      this.status.setAttribute('aria-live', 'polite');
    }

    // ---- form
    setInputValue(value) {
      this.input.value = value.slice(0, this.cfg.maxChars);
      this.updateCount();
      this.clearError();
    }

    updateCount() {
      this.count.textContent = `${this.input.value.length} / ${this.cfg.maxChars}`;
    }

    showError(message) {
      if (!this.error || !message) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }

    onSubmit(e) {
      if (e.target !== this.form) return;
      this.stopAuto();
      clearTimeout(this.debounce);

      if (!this.isCart) {
        e.preventDefault();
        this.engrave(this.input.value);
        return;
      }

      const value = this.input.value.trim();
      let message = '';
      if (!value) message = this.cfg.strings.empty;
      else if (value.length > this.cfg.maxChars) message = this.cfg.strings.tooLong;

      if (message) {
        e.preventDefault();
        e.stopPropagation();
        this.showError(message);
        this.input.focus();
        if (value !== this.text) this.engrave(value);
        return;
      }
      this.clearError();
      this.input.value = value;
      if (value !== this.text) this.engrave(value);
    }
  }

  function init(scope = document) {
    instances.forEach((inst, el) => {
      if (!el.isConnected) inst.destroy();
    });
    scope.querySelectorAll('[data-engraving-hero]').forEach((el) => {
      if (!instances.has(el)) instances.set(el, new EngravingHero(el));
    });
  }

  function teardown(scope) {
    scope.querySelectorAll('[data-engraving-hero]').forEach((el) => {
      const inst = instances.get(el);
      if (inst) inst.destroy();
    });
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => teardown(e.target));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  window.EngravingHero = EngravingHero;
})();
